import { OFF_ACTION_CLASS, OFF_OUTLINED_CLASS } from 'components/buttons/buttonStyle';
import { MenuButtonItem } from 'components/buttons/MenuButton';
import { SectionCard } from 'components/GridItemList';
import { EllipsisTooltip } from 'components/misc/EllipsisTooltip';
import {
    ChevronDownIcon,
    DownloadIcon,
    EyeIcon,
    FileCodeIcon,
    FileTextIcon,
    Loader2Icon,
    LockIcon,
    PlusIcon,
    SendIcon,
    Trash2Icon,
} from 'components/icons';
import { FormattedDate } from 'components/misc/FormattedDate';
import SendInvoicesButton from 'components/modals/invoices/SendInvoices';
import { EDIT_BUTTON_CLASS } from 'components/section-edit/SectionEdit';
import { Button } from 'components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Switch } from 'components/ui/switch';
import { Fragment, ReactNode, useId } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import { useSelector } from 'store';
import { InvoiceData, InvoiceEmailTemplateType } from 'types/invoices';
import { cn } from 'utils/cn';
import { downloadFile } from 'utils/downloadFile';
import { getClassifierText, t } from 'utils/getTranslations';
import { useClassifiers } from 'utils/useClassifiers';
import { useInvoiceDocuments } from '../useInvoiceDocuments';

const STATUS_CODES = ['INVOICE_STATUS_TYPE'];

// An icon action that is off yet a Tab stop dims its glyph, never its focus ring.
const OFF_ICON_CLASS = cn(OFF_ACTION_CLASS, 'aria-disabled:text-foreground/50 aria-disabled:hover:bg-transparent');

/** A menu entry of a document action; its glyph says what it downloads, a file unless given. */
type DocumentMenuItem = MenuButtonItem & { icon?: ReactNode; separated?: boolean };

interface ActionProps {
    label: string;
    icon: ReactNode;
    disabled: boolean;
    /** The visible line that says why the action is off: given, the action stays a Tab stop and points at it. */
    reasonId?: string;
    onClick?: () => void;
    /** A menu of these instead of a single action, its chevron saying so even while the action is off. */
    items?: DocumentMenuItem[];
}

/** A document's icon action, or the menu it opens. */
function DocumentAction({ label, icon, disabled, reasonId, onClick, items }: ActionProps) {
    const button = (
        <Button
            variant="ghost"
            size={items ? 'sm' : 'icon-sm'}
            aria-label={label}
            aria-haspopup={items ? 'menu' : undefined}
            disabled={disabled}
            focusableWhenDisabled={!!reasonId}
            aria-describedby={disabled ? reasonId : undefined}
            className={cn(OFF_ICON_CLASS, items && 'gap-0.5 px-1.5')}
            onClick={items ? undefined : onClick}
        >
            {icon}
            {items ? <ChevronDownIcon aria-hidden className="size-3" /> : null}
        </Button>
    );
    if (!items || disabled) {
        return button;
    }
    return (
        <DropdownMenu>
            <DropdownMenuTrigger render={button} />
            <DropdownMenuContent align="end" className="w-auto">
                {items.map((item) => (
                    <Fragment key={item.label}>
                        {item.separated ? <DropdownMenuSeparator /> : null}
                        <DropdownMenuItem onClick={item.onClick} className="whitespace-nowrap">
                            {item.icon ?? <FileTextIcon />}
                            {item.label}
                        </DropdownMenuItem>
                    </Fragment>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

function FileRow({ name, template, children }: { name: string; template?: string; children: ReactNode }) {
    return (
        <li className="flex items-center gap-3">
            <span
                aria-hidden
                className="bg-muted text-foreground-secondary grid size-9 shrink-0 place-items-center rounded-lg"
            >
                <FileTextIcon className="size-4" />
            </span>
            <span className="flex min-w-0 flex-auto flex-col">
                <span className="text-body1 font-medium">
                    <EllipsisTooltip>{name}</EllipsisTooltip>
                </span>
                <span className="text-caption text-foreground-tertiary">
                    <EllipsisTooltip>
                        {template ? `${t('invoices.pdf-link')} · ${template}` : t('invoices.pdf-link')}
                    </EllipsisTooltip>
                </span>
            </span>
            {children}
        </li>
    );
}

interface Props {
    invoice: InvoiceData;
    isLoading: boolean;
    templateValidation: () => Promise<boolean>;
}

/**
 * The side column's Documents: the invoice and its specification as files to preview or download, the send action
 * in the header (Send draft, Resend invoice once sent, none while Finalised), then the Disable downloads switch and
 * the sent invoice history. A file's Download is a menu of its formats, the invoice's adding XML (EST) and LEDES from
 * Finalised on; PDF alone downloads at once. While downloads are disabled every document action stays a Tab stop and
 * names the line beside the title as its reason; the sent history stays downloadable.
 */
export function InvoiceDocumentsCard({ invoice, isLoading: isLoadingOther, templateValidation }: Props) {
    const { buttonsDisabled } = useSelector((state) => state.buttons);
    const {
        sentInvoicesList,
        isLoadingPreview,
        isDeleting,
        downloadingSentInvoiceIds,
        handleDownloadSentInvoice,
        openAddSentInvoiceModal,
        isEInvoiceDownloadEnabled,
        isLedesDownloadEnabled,
        isDocxDownloadEnabled,
        hasPermissionToManageSentInvoiceHistory,
        beforeDownload,
        handleInvoicePreviewClick,
        openDownloadEInvoiceModal,
        downloadLedes,
        formatMenuItems,
        handleDelete,
        isFinalised,
        isSentOrPaid,
        isCredited,
        isFinalisedOrSentOrPaidOrCredited,
        shouldDisableButtons,
        handleToggleDownloadButtons,
    } = useInvoiceDocuments(invoice, templateValidation);
    const { classifiers } = useClassifiers({ codes: STATUS_CODES });
    const intl = useIntl();
    const reasonId = useId();
    const switchHintId = useId();
    const isLoading = isLoadingOther || buttonsDisabled;
    const isOff = shouldDisableButtons || isLoading;
    const reason = shouldDisableButtons ? reasonId : undefined;
    const sentList = sentInvoicesList ?? [];

    const invoiceName = isFinalisedOrSentOrPaidOrCredited
        ? intl.formatMessage({ id: 'invoices.side-column.invoice-file' }, { number: invoice.invoiceNumber })
        : t('invoices.summary-sidebar.draft-invoice-title');
    const specName = t('invoices.side-column.specification');
    const downloadAction = (url: string, name: string, exports: DocumentMenuItem[] = []) => {
        // PDF, DOCX under its flag, then the exports under a separator; PDF alone is no menu.
        const [pdf, docx] = formatMenuItems(url);
        const items: DocumentMenuItem[] = [
            pdf,
            ...(isDocxDownloadEnabled ? [docx] : []),
            ...exports.map((item, i) => ({ ...item, separated: i === 0 })),
        ];
        return (
            <DocumentAction
                label={intl.formatMessage({ id: 'invoices.side-column.download' }, { name })}
                icon={<DownloadIcon />}
                disabled={isOff}
                reasonId={reason}
                items={items.length > 1 ? items : undefined}
                onClick={() => {
                    void beforeDownload(() => downloadFile(url));
                }}
            />
        );
    };
    const invoiceExports: DocumentMenuItem[] = isFinalisedOrSentOrPaidOrCredited
        ? [
              ...(isEInvoiceDownloadEnabled
                  ? [
                        {
                            label: t('invoices.e-invoice-download.button'),
                            onClick: openDownloadEInvoiceModal,
                            icon: <FileCodeIcon />,
                        },
                    ]
                  : []),
              ...(isLedesDownloadEnabled
                  ? [
                        {
                            label: t('invoices.side-column.ledes-1998bi-v2'),
                            onClick: () => downloadLedes('LEDES_1998BI_V2'),
                        },
                        {
                            label: t('invoices.side-column.ledes-xml-2-1'),
                            onClick: () => downloadLedes('LEDES_XML_2_1'),
                        },
                    ]
                  : []),
          ]
        : [];

    const actions = (
        <>
            {shouldDisableButtons ? (
                // Beside the title, or on a row of its own under it where the card is too narrow for both.
                <p
                    id={reasonId}
                    className="text-caption text-warning-ink m-0 flex min-w-0 items-center gap-1.5 @max-md:order-last @max-md:basis-full"
                >
                    <LockIcon aria-hidden className="size-4 shrink-0" />
                    <FormattedMessage id="invoices.summary-sidebar.disabled-by-financial-team" />
                </p>
            ) : null}
            {isFinalised ? null : (
                <SendInvoicesButton
                    variant="outlined"
                    size="small"
                    className={cn('h-8 gap-1.5 px-2.5 ps-2 text-sm', OFF_OUTLINED_CLASS)}
                    invoices={[invoice]}
                    disabled={isOff}
                    focusableWhenDisabled={!!reason}
                    aria-describedby={reason}
                    emailTemplateType={
                        isFinalisedOrSentOrPaidOrCredited
                            ? InvoiceEmailTemplateType.INVOICE
                            : InvoiceEmailTemplateType.DRAFT_INVOICE
                    }
                >
                    <SendIcon aria-hidden className="size-3.5" />
                    <FormattedMessage
                        id={
                            isSentOrPaid || isCredited
                                ? 'invoices.summary-sidebar.resend-button'
                                : 'invoices.summary-sidebar.send-button-draft'
                        }
                    />
                </SendInvoicesButton>
            )}
        </>
    );

    return (
        <SectionCard>
            {/* SectionCard's header, wrapping, so the reason line can take a row of its own. */}
            <div className="flex min-h-[30px] flex-wrap items-center gap-x-2 gap-y-1">
                <h3 className="text-section m-0 min-w-0 flex-auto">
                    <FormattedMessage id="invoices.side-column.documents" />
                </h3>
                {actions}
            </div>
            <ul className="m-0 flex list-none flex-col gap-3 p-0">
                <FileRow name={invoiceName} template={invoice.invoiceTemplate?.name}>
                    <DocumentAction
                        label={intl.formatMessage({ id: 'invoices.side-column.preview' }, { name: invoiceName })}
                        icon={
                            isLoadingPreview ? (
                                <Loader2Icon className="motion-safe:animate-spin motion-reduce:animate-pulse" />
                            ) : (
                                <EyeIcon />
                            )
                        }
                        disabled={isOff || isLoadingPreview}
                        reasonId={reason}
                        onClick={() => {
                            void handleInvoicePreviewClick();
                        }}
                    />
                    {downloadAction(`/v1/invoices/generate/${invoice.id}`, invoiceName, invoiceExports)}
                </FileRow>
                <FileRow name={specName} template={invoice.invoiceSpecTemplate?.name}>
                    {downloadAction(`/v1/invoices/generate-spec/${invoice.id}`, specName)}
                </FileRow>
            </ul>
            {hasPermissionToManageSentInvoiceHistory && (isSentOrPaid || isCredited) ? (
                <label
                    className={cn(
                        'flex items-start gap-2.5',
                        isLoading ? 'text-foreground/50 cursor-not-allowed' : 'cursor-pointer'
                    )}
                >
                    <Switch
                        checked={invoice.downloadButtonsDisabled ?? false}
                        onCheckedChange={(checked) => {
                            void handleToggleDownloadButtons(checked);
                        }}
                        disabled={isLoading}
                        aria-describedby={switchHintId}
                        className="mt-0.5"
                    />
                    <span className="flex flex-col">
                        <span className="text-body1">
                            <FormattedMessage id="invoices.summary-sidebar.disable-downloads" />
                        </span>
                        <span id={switchHintId} className="text-caption text-foreground-tertiary">
                            <FormattedMessage id="invoices.summary-sidebar.disable-downloads-tooltip" />
                        </span>
                    </span>
                </label>
            ) : null}
            <div className="border-divider flex flex-col gap-2 border-t pt-3">
                <div className="flex min-h-[30px] items-center gap-2">
                    <h4 className="text-meta m-0 font-semibold">
                        <FormattedMessage id="invoices.summary-sidebar.sent-invoice-history" />
                    </h4>
                    <span className="text-meta text-muted-foreground flex-auto">
                        {sentList.length ? null : (
                            <>
                                <span aria-hidden>· </span>
                                <FormattedMessage id="invoices.side-column.not-sent-yet" />
                            </>
                        )}
                    </span>
                    {hasPermissionToManageSentInvoiceHistory ? (
                        <Button
                            variant="ghost"
                            size="sm"
                            className={EDIT_BUTTON_CLASS}
                            onClick={openAddSentInvoiceModal}
                            disabled={isLoading}
                        >
                            <PlusIcon data-icon="inline-start" className="size-3.5" />
                            <FormattedMessage id="invoices.add-entry" />
                        </Button>
                    ) : null}
                </div>
                {sentList.length ? (
                    <ul className="m-0 flex list-none flex-col gap-1 p-0">
                        {sentList.map((sentInvoice) => {
                            const isDownloading = downloadingSentInvoiceIds.has(sentInvoice.id);
                            return (
                                <li key={sentInvoice.id} className="text-meta flex items-center gap-2">
                                    <RouterLink
                                        to=""
                                        className={cn(
                                            'text-foreground flex-auto font-medium tabular-nums no-underline hover:underline',
                                            isDownloading && 'pointer-events-none opacity-60'
                                        )}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            if (!isDownloading) {
                                                void handleDownloadSentInvoice(sentInvoice.id);
                                            }
                                        }}
                                    >
                                        <FormattedDate value={sentInvoice.invoiceDateTime} withTime />
                                    </RouterLink>
                                    {isDownloading ? (
                                        <Loader2Icon
                                            role="progressbar"
                                            aria-label={t('loading')}
                                            className="size-3.5 motion-safe:animate-spin motion-reduce:animate-pulse"
                                        />
                                    ) : null}
                                    <span className="text-foreground-tertiary">
                                        {getClassifierText(
                                            classifiers['INVOICE_STATUS_TYPE'],
                                            sentInvoice.invoiceStatusTypeCode
                                        )}
                                    </span>
                                    {hasPermissionToManageSentInvoiceHistory ? (
                                        <Button
                                            variant="ghost"
                                            size="icon-sm"
                                            aria-label={t('delete')}
                                            onClick={() => handleDelete(sentInvoice.id)}
                                            disabled={isDeleting || isLoading}
                                        >
                                            <Trash2Icon />
                                        </Button>
                                    ) : null}
                                </li>
                            );
                        })}
                    </ul>
                ) : null}
            </div>
        </SectionCard>
    );
}
